import {Component, forwardRef, inject, Input, OnChanges} from '@angular/core';
import {FieldValueService} from '../../core/field-value.service';
import {SearchSelectorService} from '../../core/search-selector.service';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {SuperObjectModel} from '../../core/definitions/super-object-model';
import {OperationService} from '../../operations/operation.service';
import {SelectorCreationParams} from '../../core/definitions/selector-creation-params';
import {FieldParameters} from '../../core/definitions/field-parameters';
import {EditFieldSearchSelectorHandler} from "../../core/edit-field-search-selector-handler";
import {EditFieldSearchSelectorContainer} from "../../core/definitions/edit-field-search-selector-container";
import {OptionsDialogService} from "../options-dialog.service";
import {FieldType} from "../../core/definitions/field-type.enum";

@Component({
    selector: 'app-edit-field-search-selector',
    templateUrl: './edit-field-search-selector.component.html',
    styleUrls: ['./edit-field-search-selector.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => EditFieldSearchSelectorComponent)
        }
    ],
    standalone: false
})
export class EditFieldSearchSelectorComponent implements OnChanges, ControlValueAccessor {
  // Injections
  private readonly fieldValueSvc: FieldValueService = inject(FieldValueService);
  private readonly operationService: OperationService = inject(OperationService);
  private readonly searchSelectorService: SearchSelectorService = inject(SearchSelectorService);
  private readonly editFieldSearchSelectorHandler: EditFieldSearchSelectorHandler = inject(EditFieldSearchSelectorHandler);
private readonly optionsDialogService: OptionsDialogService = inject(OptionsDialogService);

  @Input() fieldParameters: FieldParameters;
  @Input() formControlName: string;
  @Input() disabled: boolean;

  editFieldSearchSelectorContainer: EditFieldSearchSelectorContainer;
  openModal = false;

  ngOnChanges() {
    this.editFieldSearchSelectorContainer = this.editFieldSearchSelectorHandler.initialize(this.fieldParameters, this.formControlName);
  }

  clickEnableSelector() {
    const selector = this.editFieldSearchSelectorContainer.reference.selector;
    const container = this.operationService.currentOperationContainer;
    const selected = this.getSelectedValues();
    const params = {
      selected: selected,
      used: this.getUsedValues(selected, selector.item_removed_property)
    } as SelectorCreationParams;
    // TODO: Add template group id to enableSelector call
    this.searchSelectorService.enableSelector(selector, container, params, {
      searchContainerCreated: (/*sc*/) => {
      },
      selectorCallback: (selectedObj: Array<SuperObjectModel>) => {
        this.searchSelectorService.disableSelector(container);
        const artifactId = selectedObj[0].artifact_id;
        const artifactName = selectedObj[0].artifact_name;
        this.editFieldSearchSelectorHandler.setSingleValue(this.editFieldSearchSelectorContainer, artifactId, artifactName);
      }
    });
  }

  openCreateNew() {
    this.editFieldSearchSelectorHandler.openCreateNew(this.editFieldSearchSelectorContainer);
  }

  searchKulturNav() {
    this.openModal = true;
    this.optionsDialogService.searchKulturNav(
      this.fieldParameters.field,
      this.getIsArray(),
      '',
      this.fieldParameters?.sectionsContainer?.parentObject?.superobject_type_id).then(options => {
        this.editFieldSearchSelectorHandler.setSingleValue(
          this.editFieldSearchSelectorContainer, options[0].artifact_id, options[0].artifact_name);
    }, () => {
      this.openModal = false;
    });
  }

  clearValue() {
    this.editFieldSearchSelectorHandler.setSingleValue(
      this.editFieldSearchSelectorContainer, null, ''
    );
  }

  get isSingleItemArray() {
    return this.fieldValueSvc.isSingleItemArray(this.fieldParameters);
  }

  private getIsArray() {
    return this.fieldParameters.field.field_type === FieldType.ARRAY &&
      !this.isSingleItemArray;
  }

  private getUsedValues(selected: any, itemRemovedProperty: string) {
    let parentArray = this.fieldParameters.sectionsContainer.rootObject[this.fieldParameters.field.parent_name];
    const res = [];
    if (parentArray) {
      if (!Array.isArray(parentArray)) {
        parentArray = [parentArray];
      }
      for (const item of parentArray) {
        const value = item[this.fieldParameters.field.name];
        if (value && selected.indexOf(value) === -1 && (!itemRemovedProperty || !item[itemRemovedProperty])) {
          res.push(value);
        }
      }
    }
    res.push(this.fieldParameters.sectionsContainer.rootObject.artifact_id);
    return res;
  }

  private getSelectedValues() {
    const res = [];
    const value = this.fieldValueSvc.getFieldValue(this.fieldParameters.object, this.fieldParameters.field.name);
    if (value) {
      res.push(value);
    }
    return res;
  }

  registerOnChange(/*fn: any*/): void {
    // N/A
  }

  registerOnTouched(/*fn: any*/): void {
    // N/A
  }

  setDisabledState(/*isDisabled: boolean*/): void {
    // N/A
  }

  writeValue(/*obj: any*/): void {
    // N/A
  }


}
